const hasLimit = (value) =>
  value !== undefined && value !== null && value !== "";
const farmId = (item) =>
  `${item.item}${item.description}${item.farmName}`.replace(/\s/g, "");
const pantryKey = (item) =>
  `${item.item}${item.individualDescription}${item.farmName}`;

function stockRequests(database, items) {
  const requests = new Map();
  for (const item of items) {
    const quantity = Number(item.quantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      throw new Error(
        "Please refresh your basket and check the item quantities.",
      );
    }
    const pantry = hasLimit(item.limit);
    if (!pantry && !hasLimit(item.farmLimit)) continue;
    if (pantry && !item.pantryLocation) {
      throw new Error(
        "A pantry location is unavailable. Please refresh your basket.",
      );
    }
    const reference = pantry
      ? database.collection("Pantry").doc(item.pantryLocation)
      : database.collection("FoodItems").doc(farmId(item));
    if (!requests.has(reference.path)) {
      requests.set(reference.path, { reference, pantry, items: [] });
    }
    requests.get(reference.path).items.push(item);
  }
  return [...requests.values()];
}

function remainingStock(available, quantity, item) {
  const stock = Number(available);
  if (!Number.isFinite(stock) || stock < quantity) {
    throw new Error(
      `There are only ${Number.isFinite(stock) ? stock : 0} ${
        item.item
      } remaining. Please refresh your basket.`,
    );
  }
  return stock;
}

function stockUpdates(request, snapshot) {
  const updates = {};
  const data = snapshot.exists ? snapshot.data() : {};
  for (const item of request.items) {
    const key = request.pantry ? pantryKey(item) : "farmLimit";
    const quantity = Number(item.quantity);
    const current = request.pantry
      ? updates[key]?.limit ?? data[key]?.limit
      : updates.farmLimit ?? data.farmLimit;
    const available = remainingStock(current, quantity, item);
    if (request.pantry) {
      updates[key] = { ...data[key], limit: available - quantity };
      continue;
    }
    const reserved = hasLimit(item.guaranteedMaxQuantity)
      ? Math.min(quantity, Number(item.guaranteedMaxQuantity))
      : quantity;
    if (!Number.isFinite(reserved) || reserved < 0) {
      throw new Error(
        "A product limit is unavailable. Please refresh your basket.",
      );
    }
    updates.farmLimit = available - reserved;
  }
  return updates;
}

function participationCredits(profile, requested) {
  const charge = Number(requested);
  const available = Number(profile.credits ?? 0);
  if (!Number.isFinite(charge) || charge < 0 || !Number.isFinite(available)) {
    throw new Error(
      "Participation credits are unavailable. Please refresh your basket.",
    );
  }
  const chargeHundredths = Math.round(charge * 100);
  // Orders using no participation credits must leave the balance unchanged.
  if (chargeHundredths === 0) return {};
  const availableHundredths = Math.round(available * 100);
  if (chargeHundredths > availableHundredths) {
    throw new Error(
      "Your participation credits changed. Please refresh your basket.",
    );
  }
  return { credits: (availableHundredths - chargeHundredths) / 100 };
}

function basketSignature(items = []) {
  return JSON.stringify(
    items
      .map((item) =>
        JSON.stringify([
          item.item,
          item.description,
          item.farmName,
          Number(item.quantity),
        ]),
      )
      .sort(),
  );
}

/** Commit all customer checkout mutations together. Retries have no UI/email effects. */
export default async function CommitIndividualOrder({
  database,
  userReference,
  orderReference,
  groups,
  distributionDate,
  orderData,
  userInfo,
  fromBasket,
}) {
  const candidates = await groups
    .where("distributionDate", "==", distributionDate)
    .where("orderCount", "<", 40)
    .limit(1)
    .get();
  const newGroup = groups.doc(orderData.orderDate);
  const candidate = candidates.empty ? newGroup : candidates.docs[0].ref;
  const requests = stockRequests(database, orderData.foodList);
  const orderKey = `${userInfo.firstName}-${userInfo.lastName}-${userReference.id}-${orderData.orderDate}`;

  return database.runTransaction(async (transaction) => {
    const existingOrder = await transaction.get(orderReference);
    const profileSnapshot = await transaction.get(userReference);
    if (!profileSnapshot.exists)
      throw new Error("Your account is unavailable. Please sign in again.");
    const profile = profileSnapshot.data();
    // A retried operation with this order reference must not deduct stock or credits twice.
    if (existingOrder.exists) return { order: existingOrder.data(), profile };
    if (
      fromBasket &&
      basketSignature(profile.basketItems) !==
        basketSignature(userInfo.basketItems)
    ) {
      throw new Error(
        "Your basket changed. Please refresh it before placing your order.",
      );
    }
    const candidateSnapshot = await transaction.get(candidate);
    const fallbackSnapshot =
      candidate.path === newGroup.path
        ? candidateSnapshot
        : await transaction.get(newGroup);
    const candidateCount = Number(candidateSnapshot.data()?.orderCount ?? 0);
    const useCandidate = candidateCount < 40;
    const communityReference = useCandidate ? candidate : newGroup;
    const count = useCandidate
      ? candidateCount
      : Number(fallbackSnapshot.data()?.orderCount ?? 0);
    if (!Number.isFinite(count) || count < 0 || count >= 40) {
      throw new Error(
        "The pickup queue changed. Please try placing your order again.",
      );
    }
    const stocks = [];
    for (const request of requests) {
      const snapshot = await transaction.get(request.reference);
      stocks.push({ ...request, updates: stockUpdates(request, snapshot) });
    }
    const profileUpdates = {
      ...participationCredits(profile, orderData.creditPortion),
      ...(fromBasket ? { basketItems: [] } : {}),
    };
    for (const stock of stocks) {
      transaction.set(stock.reference, stock.updates, { merge: true });
    }
    if (Object.keys(profileUpdates).length)
      transaction.update(userReference, profileUpdates);
    const { pickupLocation, contactMethods, ...communityOrder } = orderData;
    const savedOrder = {
      ...orderData,
      confirmationStatus: "pending",
      participationCredits: {
        charged: Number(orderData.creditPortion),
        balanceBefore: Number(profile.credits ?? 0),
        balanceAfter: profileUpdates.credits ?? Number(profile.credits ?? 0),
      },
    };
    transaction.set(orderReference, savedOrder);
    transaction.set(
      communityReference,
      {
        distributionDate,
        orderCount: count + 1,
        [orderKey]: communityOrder,
      },
      { merge: true },
    );
    return { order: savedOrder, profile: { ...profile, ...profileUpdates } };
  });
}
